import { useEffect, useLayoutEffect, useRef } from "react";

import { FLIP_ID } from "../flip.js";

/**
 * The highlight on whatever is open in a list (the bot's chat, the settings
 * page, the tab), drawn once for the whole list instead of on each item: it
 * glides from the one left to the one picked, as the context menu's follows
 * the pointer. It goes first inside the list (which must be positioned) and
 * finds the open item there by `selector`; it lies under items that are
 * positioned themselves, and they leave their own selected background to it.
 *
 * It is measured every render of the list, when the container resizes, and
 * when a transition or an animation in it ends (a section unfolding). When
 * the open item is itself sliding to a new place (flip.ts), it rides along
 * with it frame by frame rather than racing ahead to where it will land.
 * With nothing open, or the open item out of sight, it fades away, and it
 * comes back in place rather than sliding in from wherever it was last.
 */
export function ActiveGlide({ selector, className, item = false }: { readonly selector: string; readonly className: string; /** Inside a list (`ul`), drawn as an item of it with no role of its own. */ readonly item?: boolean }): React.JSX.Element {
  const bar = useRef<HTMLElement>(null);
  const shown = useRef(false);
  const frame = useRef(0);

  const measure = (): void => {
    const glide = bar.current;
    const box = glide?.parentElement;
    if (box == null || glide == null) return;
    const target = box.querySelector<HTMLElement>(selector);
    if (target == null || target.offsetParent == null) {
      glide.classList.remove("shown");
      shown.current = false;
      return;
    }
    const place = (): void => {
      const outer = box.getBoundingClientRect();
      const inner = target.getBoundingClientRect();
      const x = inner.left - outer.left - box.clientLeft + box.scrollLeft;
      const y = inner.top - outer.top - box.clientTop + box.scrollTop;
      glide.style.transform = `translate(${x}px, ${y}px)`;
      glide.style.width = `${inner.width}px`;
      glide.style.height = `${inner.height}px`;
    };
    cancelAnimationFrame(frame.current);
    if (!shown.current) {
      glide.style.transition = "none";
      place();
      glide.getBoundingClientRect();
      glide.style.transition = "";
      glide.classList.add("shown");
      shown.current = true;
    } else {
      place();
    }
    // The list's own slides start after this measure; the next frame shows whether the open item is in one.
    frame.current = requestAnimationFrame(() => {
      // The item slides, or the group it is in does (a section moved, its bots riding with it).
      const movers: Element[] = [];
      for (let at: Element | null = target; at != null && at !== box; at = at.parentElement) movers.push(at);
      const sliding = (): boolean => movers.some((mover) => mover.getAnimations().some((one) => one.id === FLIP_ID && one.playState === "running"));
      if (!sliding()) return;
      glide.style.transition = "none";
      const ride = (): void => {
        place();
        if (sliding()) {
          frame.current = requestAnimationFrame(ride);
          return;
        }
        glide.style.transition = "";
      };
      ride();
    });
  };

  useLayoutEffect(measure);

  useEffect(() => {
    const box = bar.current?.parentElement;
    if (box == null) return;
    const again = (event: Event): void => {
      if (event.target !== bar.current) measure();
    };
    const resize = new ResizeObserver(() => measure());
    resize.observe(box);
    box.addEventListener("transitionend", again);
    box.addEventListener("animationend", again);
    return () => {
      resize.disconnect();
      box.removeEventListener("transitionend", again);
      box.removeEventListener("animationend", again);
      cancelAnimationFrame(frame.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- measure reads the refs; the listeners live as long as the list
  }, []);

  if (item) return <li ref={(node) => void (bar.current = node)} role="presentation" className={`active-glide ${className}`} aria-hidden="true" />;
  return <div ref={(node) => void (bar.current = node)} className={`active-glide ${className}`} aria-hidden="true" />;
}
